import { datadogLogs } from '@datadog/browser-logs';
import { Box, Hidden, Snackbar, Typography, withStyles } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { isEmpty } from 'lodash';
import queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { setPartnerMeta } from '../actions';
import {
    fetchLocationEligibilityEncrypted,
    fetchPartnerMeta,
    getAvailabilityEncrypted,
    scheduleVisitEncrypted,
} from '../api';
import { AxleButton, AxlePaymentBox } from '../components';
import ClinicianContainer from '../components/Book/ClinicianContainer';
import { keysSnakeToCamel } from '../utils';

const styles = theme => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    leftContainer: {
        display: 'flex',
        flexDirection: 'column',
        flex: 7,
        padding: theme.spacing(2), // Default the whole page to have some padding
        paddingTop: theme.spacing(5), // But override the top padding to be a bit larger.
        borderRight: '1px dashed lightgray',
        paddingRight: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            borderRight: '0',
            borderBottom: '1px dashed lightgray',
        },
    },
    rightContainer: {
        display: 'flex',
        flex: 3,
        flexDirection: 'column',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            alignItems: 'center',
            flex: 0,
        },
    },
    paymentBox: {
        marginRight: theme.spacing(4),
        marginTop: theme.spacing(6),
        [theme.breakpoints.down('sm')]: {
            marginTop: theme.spacing(2),
        },
    },
    payCta: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            width: '100%',
            position: 'fixed',
            height: '48px',
            bottom: 0,
            left: 0,
            zIndex: 9998,
        },
    },
    snackbar: {
        zIndex: 9999,
    },
});

class BookWithClinician extends React.Component {
    state = {
        availability: null,
        clinician: null,
        visitTime: new Set(),
        metadata: null,
        isLoadingTimesgrid: false,
        isPaymentLoading: false,
        snackbar: { show: false, message: '', severity: 'error' },
        formErrors: {},
        visitMeta: {},
        patientMeta: {},
        partnerId: null,
        data: null,
        timezone: null,
    };

    constructor(props) {
        super(props);
        this.whoRef = React.createRef();
        this.whatRef = React.createRef();
        this.whereRef = React.createRef();
    }

    async componentDidMount() {
        this.setContentFromQueryParams();
    }

    setContentFromQueryParams = async () => {
        const params = keysSnakeToCamel(queryString.parse(this.props.location.search));

        const partnerId = params.orgId;

        const { result } = await fetchPartnerMeta(partnerId);
        if (result) {
            this.props.setPartnerMeta(result);
        }

        if (partnerId) this.setState({ partnerId });

        const data = params.xdata;
        if (data) this.setState({ data });

        fetchLocationEligibilityEncrypted({
            data,
            partnerId,
        }).then(() => {
            this.handleShowTimesgrid();
        });
    };

    handleClinicianDatetimeSelected = (time, clinician) => {
        const currentlySelectedTimes = new Set();
        currentlySelectedTimes.add(time);
        this.setState({ visitTime: currentlySelectedTimes });
        this.setState({ clinician });
    };

    handleShowTimesgrid = async () => {
        // NOTE: The tier is set after the location eligibility check. Logically, we shouldn't be able
        //  to check for available times, if we don't yet know whether or not the zip code is serviced.
        //  Maybe we make a "didPassLocationEligibilityCheck" but I don't want to add anything new in until
        //  we've fixed the rest of this product.
        this.setState({ isLoadingTimesgrid: true });

        let result, helpText, timezone, error;
        ({ result, helpText, timezone, error } = await getAvailabilityEncrypted({
            partnerId: this.state.partnerId,
            data: this.state.data,
        }));

        if (result) {
            if (result.length === 0) {
                datadogLogs.logger.warn(
                    'No clinicians returned for that service at that location',
                    {
                        page: 'book-with-clinician',
                    }
                );
            }
            this.setState({
                availability: result,
                helpText: helpText,
                isLoadingTimesgrid: false,
                timezone,
            });
        } else {
            this.setState({
                isLoadingTimesgrid: false,
                snackbar: { show: true, message: error, severity: 'error' },
            });
        }
    };

    handlePaymentClick = async () => {
        this.setState({ isPaymentLoading: true });
        const { data, visitTime, clinician, partnerId } = this.state;

        let error, result;

        ({ result, error } = await scheduleVisitEncrypted({
            data,
            visitTime,
            partnerId,
            clinicianId: clinician?.uuid,
        }));

        if (result) {
            this.props.history.push(`/confirmation/${result.visit_id}`);
        } else {
            this.setState({
                isPaymentLoading: false,
                snackbar: { show: true, message: error, severity: 'error' },
            });
        }
    };

    canProceedToPayment = () => {
        const { visitTime } = this.state;
        const hasVisitTime = visitTime.size >= 1;
        return hasVisitTime && isEmpty(this.state.formErrors);
    };

    getLineItems = () => {
        const lineItems = [];

        const flatLineItems = lineItems.flat();

        const quantities = {};
        for (const li of flatLineItems) {
            const id = li.id;
            if (id in quantities) {
                const q = quantities[id];
                quantities[id] = q + 1;
            } else {
                quantities[id] = 1;
            }
        }

        const lineItemsWithQuantity = [];
        const added = new Set();
        for (const lineItem of flatLineItems) {
            if (!added.has(lineItem.id)) {
                added.add(lineItem.id);
                lineItemsWithQuantity.push({
                    ...lineItem,
                    // This list is ultimately passed to <AxlePaymentBox> which expects price
                    //  to be one of the attributes. Removing price from the line item object
                    //  does NOT seem to break anything but leaving here just in case. It will
                    //  always be 0 since we removed payment functionality.
                    price: 0,
                    quantity: quantities[lineItem.id],
                });
            }
        }
        return lineItemsWithQuantity;
    };

    handleCloseSnackbar = () => {
        this.setState({
            snackbar: { show: false, message: '', severity: 'error' },
        });
    };

    validateForm = () => {
        return true;
    };

    getContainers = () => {
        const params = keysSnakeToCamel(queryString.parse(this.props.location.search));
        const { availability, clinician, visitTime, timezone } = this.state;
        const containers = [];

        containers.push({
            renderFunc: idx => (
                <ClinicianContainer
                    key={idx}
                    availabilityByClinician={availability}
                    selectedClinician={clinician}
                    visitTime={visitTime}
                    onVisitTimeSelected={this.handleClinicianDatetimeSelected}
                    timezone={timezone}
                    forceOpen={true}
                />
            ),
        });

        return containers;
    };

    render() {
        const { classes } = this.props;
        const lineItems = this.getLineItems();
        const containers = this.getContainers();

        return (
            <div className={classes.root}>
                <div className={classes.leftContainer}>
                    {containers.map((container, i) => container.renderFunc(i))}
                </div>
                {/* Shown on desktop */}
                <div className={classes.rightContainer}>
                    <AxlePaymentBox
                        className={classes.paymentBox}
                        ctaTitle='Book Visit'
                        title='Visit Summary'
                        lineItems={lineItems}
                        supportEmail={this.props.partnerMeta?.support_email}
                        supportPhone={this.props.partnerMeta?.support_phone}
                        useSupportPhoneOverEmail={true}
                        onClick={this.handlePaymentClick}
                        orderTotal={0}
                        disabled={!this.canProceedToPayment()}
                        isLoading={this.state.isPaymentLoading}
                        validateForm={this.validateForm}
                        shouldShowSummary={false}
                    />
                </div>
                {/* Show on mobile */}
                <Hidden mdUp>
                    <Box
                        onClick={this.validateForm}
                        onMouseEnter={this.validateForm}
                        className={classes.payCta}
                    >
                        <AxleButton
                            isLoading={this.state.isPaymentLoading}
                            className={classes.payCta}
                            onClick={this.handlePaymentClick}
                            trackingEvent={{ name: 'Book' }}
                            disabled={!this.canProceedToPayment()}
                        >
                            <Typography variant='button'>Book Visit</Typography>
                        </AxleButton>
                    </Box>
                </Hidden>
                <Snackbar
                    className={classes.snackbar}
                    open={this.state.snackbar.show}
                    autoHideDuration={5000}
                    onClose={this.handleCloseSnackbar}
                >
                    <MuiAlert
                        onClose={this.handleCloseSnackbar}
                        severity={this.state.snackbar.severity}
                    >
                        {this.state.snackbar.message}
                    </MuiAlert>
                </Snackbar>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    zipCode: state.zipCode,
    city: state.city,
    usState: state.usState,
    partnerId: state.partnerId,
    presetServices: state.presetServices,
    partnerMeta: state.partnerMeta,
    visitRequestId: state.visitRequestId,
    tier: state.tier,
});

const mapDispatchToProps = dispatch => ({
    setPartnerMeta: val => dispatch(setPartnerMeta(val)),
});

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(BookWithClinician))
);
